/** Mirrors the backend `FletchDocumentStatus` enum; keep the two in sync. */
export enum FileStatus {
  REGISTERED = 'REGISTERED',
  UPLOADING = 'UPLOADING',
  /** Uploaded and waiting for the malware scan's verdict; `UPLOADED` follows a clean one. */
  SCANNING = 'SCANNING',
  INFECTED = 'INFECTED',
  /** No verdict arrived in time, so the backend gave up on the document. */
  SCAN_FAILED = 'SCAN_FAILED',
  UPLOADED = 'UPLOADED',
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONTENT_EXTRACTION_IN_PROGRESS = 'CONTENT_EXTRACTION_IN_PROGRESS',
  CONTENT_EXTRACTION_IN_PROGRESS_PDF_SAVED = 'CONTENT_EXTRACTION_IN_PROGRESS_PDF_SAVED',
  CONTENT_EXTRACTION_COMPLETE = 'CONTENT_EXTRACTION_COMPLETE',
  CONTENT_EXTRACTION_FAILED = 'CONTENT_EXTRACTION_FAILED',
  INDEX_GENERATION_IN_PROGRESS = 'INDEX_GENERATION_IN_PROGRESS',
  INDEX_GENERATION_FAILED = 'INDEX_GENERATION_FAILED',
  INDEX_GENERATION_COMPLETE = 'INDEX_GENERATION_COMPLETE',
  FINDING_SUB_DOCUMENTS_IN_PROGRESS = 'FINDING_SUB_DOCUMENTS_IN_PROGRESS',
  FINDING_SUB_DOCUMENTS_FAILED = 'FINDING_SUB_DOCUMENTS_FAILED',
  FINDING_SUB_DOCUMENTS_COMPLETE = 'FINDING_SUB_DOCUMENTS_COMPLETE',
  SUMMARISING_CLUSTERS_IN_PROGRESS = 'SUMMARISING_CLUSTERS_IN_PROGRESS',
  SUMMARISING_CLUSTERS_FAILED = 'SUMMARISING_CLUSTERS_FAILED',
  SUMMARISING_CLUSTERS_COMPLETE = 'SUMMARISING_CLUSTERS_COMPLETE',
  READY_FOR_CHAT = 'READY_FOR_CHAT',
  /** Terminal: the pipeline will not extract this document's content, so it never becomes chat-ready. */
  WONT_EXTRACT_CONTENT = 'WONT_EXTRACT_CONTENT',
}

/**
 * Mirrors the backend `DocumentSource` enum; keep the two in sync. Fletch-generated
 * files have no code yet — see `getCaseFileSourceDisplay` for unmapped codes.
 */
export enum FileSource {
  PROCLAIM = 'PROCLAIM',
  UPLOAD = 'UPLOAD',
}

export const ERROR_STATUS_VALUES = [
  FileStatus.UPLOAD_FAILED,
  FileStatus.INFECTED,
  FileStatus.SCAN_FAILED,
  FileStatus.VALIDATION_ERROR,
  FileStatus.CONTENT_EXTRACTION_FAILED,
  FileStatus.INDEX_GENERATION_FAILED,
  FileStatus.WONT_EXTRACT_CONTENT,
];

/**
 * An uploaded chat document the backend is still working on: poll it, and hold back a
 * prompt that attaches it until it is `READY_FOR_CHAT`. Anything that is neither ready nor
 * failed counts, so a status the FE doesn't know yet can't let a prompt through.
 */
export const isProcessingStatus = (status: string): boolean =>
  status !== FileStatus.UPLOADING &&
  status !== FileStatus.READY_FOR_CHAT &&
  !ERROR_STATUS_VALUES.includes(status as FileStatus);

/** Statuses of a document that is either usable or still on its way there. */
export const VALID_STATUS_VALUES = [
  FileStatus.UPLOADING,
  FileStatus.SCANNING,
  FileStatus.READY_FOR_CHAT,
  FileStatus.UPLOADED,
  FileStatus.CONTENT_EXTRACTION_IN_PROGRESS,
  FileStatus.CONTENT_EXTRACTION_IN_PROGRESS_PDF_SAVED,
  FileStatus.CONTENT_EXTRACTION_COMPLETE,
  FileStatus.INDEX_GENERATION_IN_PROGRESS,
  FileStatus.INDEX_GENERATION_COMPLETE,
  FileStatus.FINDING_SUB_DOCUMENTS_IN_PROGRESS,
  FileStatus.FINDING_SUB_DOCUMENTS_COMPLETE,
  FileStatus.SUMMARISING_CLUSTERS_IN_PROGRESS,
  FileStatus.SUMMARISING_CLUSTERS_COMPLETE,
];
